import React, { useMemo, useState } from 'react';
import {
  Card,
  Badge,
  Row,
  Col,
  Spinner,
  Form,
  Button,
  ProgressBar
} from 'react-bootstrap';
import { useQuery, useQueries } from '@tanstack/react-query';
import { http } from '../../../features/jobs/api/http';

// At most one decimal, on purpose. A reel with 13.508 m left printed itself as
// "13.508 m available" next to figures like "5,251.921" — and read as thirteen
// THOUSAND metres, which is how a reel with 13 m on it looked like the biggest
// one on the floor. One decimal cannot be mistaken for a thousands group in
// either locale.
const fmt = n =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

async function fetchCompatible(params) {
  const { data } = await http.get('/api/redis/phantoms/compatible', { params });
  return data.phantoms || [];
}

// Rope that is still being made. Matched by the same wireType + section rule as
// the finished reels above — see redis_dev/helpers/ropeStock.js.
async function fetchIncomingRopes(params) {
  const { data } = await http.get('/api/redis/phantoms/incoming-ropes', { params });
  return data.drums || [];
}

// ── A rope drum that does not exist yet ────────────────────────────────────
//
// 2026-11-04: *"acuma pe linie se creeaza un COPPER_ROPE_FLEXIBLE 1x6 la
// dtb1250, si inca unul in queue … dar in incoming execution nu imi apare care
// sunt in LUCRU ACTIVE LA MASINA, si in PENDING."* Then, once they appeared:
// *"nu pot sa selectez si sa ma folosesc de ele ca si cele deja done … trebuie
// sa pot sa ma folosesc de ele cand lansez chiar daca ele nu sunt gata."*
//
// So it IS selectable — but not as a phantom. A phantom execution is born
// COMPLETED holding metres that exist; nothing is measured on this drum yet, so
// claiming it that way would invent them.
//
// What this is instead is a CLAIM on part of that drum's output — a stock reel
// one step earlier in time. The rope is ALREADY LAUNCHED and already in the
// DTB1250's queue, so nothing is scheduled: the execution carries no machine
// and the server writes one `reservedBy` row on the donor's token. The drum is
// wound exactly as it was planned, and its output is handed over when it comes
// off. helpers/ropeClaimSelection.js, redis_dev/helpers/ropeClaims.js.
//
// It was a SHARE first (`drumShares.js`), which divides the drum between its
// owners — and that re-planned both COPPER_ROPE jobs to a lot of 0 and put two
// MYYM runs in the DTB1250 queue for metres already being wound (2026-11-04).
//
// Amber, not the stock cyan: the metres are not here yet, and the card says
// which drum they will come off. The badges are the words Project Token State
// uses for the same two facts.
const IncomingRopeCard = ({ drum, selected, amount, onToggle, onAmount, takenBy = [] }) => {
  const running = drum.status === 'ACTIVE';
  // What is spoken for on the drum before this launch touched it: reserved
  // downstream or already drawn off. `takenByOthers` is this launch's own
  // doing and is listed separately, by name.
  const promised = Math.max(
    0,
    drum.plannedLength - drum.freeLength - Number(drum.takenByOthers || 0)
  );
  // What is still unclaimed on the drum after this row. The donor's own plan
  // does not move — it is not being divided — so this is what the NEXT product
  // will be offered, not what the rope lot is left holding.
  const stillFree = Math.max(0, drum.freeLength - (Number(amount) || 0));

  return (
    <div
      className={`p-2 rounded border h-100 ${
        selected ? 'border-success bg-success-subtle' : 'border-warning bg-white'
      }`}
      style={{ fontSize: 12 }}
    >
      <div
        className="d-flex justify-content-between align-items-center mb-1"
        style={{ cursor: onToggle ? 'pointer' : 'default' }}
        onClick={onToggle ? () => onToggle(drum) : undefined}
      >
        <Badge
          bg={selected ? 'success' : running ? 'warning' : 'secondary'}
          style={{ fontSize: 10 }}
        >
          {selected
            ? 'SELECTED'
            : running
              ? `● ON ${String(drum.machineId || '').toUpperCase() || 'LINE'}`
              : 'QUEUED · NOT MADE YET'}
        </Badge>
        <span className="fw-bold">
          {fmt(drum.freeLength)} m <span className="text-muted fw-normal">planned</span>
        </span>
      </div>

      {running && (
        <>
          <ProgressBar
            now={drum.progress}
            variant="warning"
            style={{ height: 4 }}
            className="mb-1"
          />
          <div className="text-700 mb-1" style={{ fontSize: 10 }}>
            {fmt(drum.producedLength)} m made so far
          </div>
        </>
      )}

      <div className="text-muted mb-1" style={{ fontSize: 11 }}>
        {drum.wireType && <span className="me-1">{drum.wireType}</span>}
        sect. {drum.section}
        {drum.machineId && !running && (
          <span className="ms-1">· {String(drum.machineId).toUpperCase()}</span>
        )}
      </div>

      {promised > 0 && (
        <div className="text-warning mb-1" style={{ fontSize: 10 }}>
          {fmt(promised)} m of it is already promised
        </div>
      )}

      {/* WHO ELSE IN THIS LAUNCH IS ON IT. Without this the planner could not
          tell an untouched drum from one another product had half spent — the
          card read the same either way, and the two shares together would have
          re-planned the bobbin (2026-11-04). */}
      {takenBy.length > 0 && (
        <div className="mb-1" style={{ fontSize: 10 }}>
          {takenBy.map((t, i) => (
            <div key={i} className={t.own ? 'text-info' : 'text-warning'}>
              {fmt(t.metres)} m → {t.own ? 'this phase' : t.label}
            </div>
          ))}
          <div className="text-700">
            {fmt(drum.freeLength)} m left of {fmt(drum.plannedLength)} m
          </div>
        </div>
      )}

      <div className="text-muted" style={{ fontSize: 10 }}>
        {drum.jobCode || drum.productLabel || drum.jobId} · {drum.tokenId}
      </div>

      {selected && (
        <>
          <Form.Group className="mt-1">
            <Form.Label className="mb-0" style={{ fontSize: 10 }}>
              Take (m):
            </Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              max={drum.freeLength}
              value={amount || ''}
              onChange={e => onAmount(drum, e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12 }}
            />
          </Form.Group>
          {/* What is left of the drum for everybody else. The rope lot keeps
              its whole plan — the drum is not divided — so the number that
              matters here is how much of its output is still unclaimed. */}
          <div className="text-700 mt-1" style={{ fontSize: 10 }}>
            <b>{fmt(stillFree)} m</b> of the drum still unclaimed
            {Number(amount) > drum.freeLength && (
              <span className="text-danger ms-1">
                — this is more than the drum has left; the launch will refuse it
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// check if a phantom's wire specs are compatible with ANY of the product's conductors
function isWireCompatible(phantom, wireSpecs) {
  if (!wireSpecs || !wireSpecs.length) return true;

  const pType = String(phantom.wireType || '').toUpperCase();

  const pDiams = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.map(Number).sort((a, b) => a - b)
    : phantom.wireDiameter != null
      ? [Number(phantom.wireDiameter)]
      : [];

  const pCounts = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.map(Number).sort((a, b) => a - b)
    : phantom.numberOfWires != null
      ? [Number(phantom.numberOfWires)]
      : [];

  for (const spec of wireSpecs) {
    const sType = String(spec.wireType || '').toUpperCase();
    if (pType !== sType) continue;

    const sDiams = Array.isArray(spec.wireDiameter)
      ? spec.wireDiameter.map(Number).sort((a, b) => a - b)
      : spec.wireDiameter != null
        ? [Number(spec.wireDiameter)]
        : [];

    if (pDiams.length !== sDiams.length) continue;
    const diamsMatch = pDiams.every((d, i) => Math.abs(d - sDiams[i]) < 0.01);
    if (!diamsMatch) continue;

    if (pCounts.length && spec.numberOfWires) {
      const sCounts = Array.isArray(spec.numberOfWires)
        ? spec.numberOfWires.map(Number).sort((a, b) => a - b)
        : [Number(spec.numberOfWires)];
      if (pCounts.length !== sCounts.length) continue;
      const countsMatch = pCounts.every((c, i) => c === sCounts[i]);
      if (!countsMatch) continue;
    }

    return true;
  }

  return false;
}

// ── Single-select phantom card (original behavior for unwinding, conductor_isolation) ──
const SingleSelectCard = ({ phantom, onUsePhantom }) => {
  const wType = String(phantom.wireType || '').toUpperCase();
  const wDiam = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.join(', ')
    : phantom.wireDiameter || '';
  const wCount = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.join(', ')
    : phantom.numberOfWires || '';
  const showAvail = phantom.effectiveAvailable ?? phantom.availableLength;

  return (
    <div
      className="p-2 rounded border border-info bg-white"
      style={{ cursor: 'pointer', fontSize: 12 }}
      onClick={() => onUsePhantom && onUsePhantom({ ...phantom, availableLength: showAvail })}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <Badge bg="info" style={{ fontSize: 10 }}>PHANTOM</Badge>
        <span className="fw-bold">{fmt(showAvail)} m</span>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {phantom.color && <Badge bg="dark" className="me-1">{phantom.color}</Badge>}
        {wType && <span className="me-1">{wType}</span>}
        {wDiam && <span className="me-1">Ø{wDiam}</span>}
        {wCount && <span className="me-1">×{wCount}</span>}
      </div>
      {phantom.drum_material && (
        <div className="text-muted" style={{ fontSize: 10 }}>
          {phantom.drum_material} {phantom.drum_diameter && `Ø${phantom.drum_diameter}`}
        </div>
      )}
      {phantom.note && (
        <div className="text-muted mt-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
          {phantom.note}
        </div>
      )}
    </div>
  );
};

// ── Multi-select phantom card for pre_stranding (stock reels) ──
//
// `ropeSpecs` present = a cable stranded twice (a reduced earth). Then every
// figure here is PER ROPE: the 35 mm² rope needs `length × cores` and the
// 16 mm² earth needs `length`, and a reel of one is no use to the other. One
// picker still handles both, because confirming replaces every phantom
// execution on the phase at once — two pickers would each wipe the other's.
const MultiSelectPicker = ({
  available,
  totalNeeded,
  onUsePhantoms,
  usedPhantomIds,
  ropeSpecs = null,
  // What THIS phase has already committed, per reel. Confirming replaces every
  // phantom execution on the phase at once, so the committed amount is shown as
  // a live selection rather than subtracted away: otherwise a planner who took
  // 5 150 m of a 5 163.5 m reel saw a green SELECTED card reading "13.5 m
  // available" and no way to correct the figure downwards.
  ownAlloc = {},
  takenBy = {},
  // Metres this phase has covered OUTSIDE this picker — shares of rope still
  // being wound. Without it the header read "87 500 of 105 000 · rest will be
  // produced" with the other 17 500 already taken off a drum on the DTB1250.
  alsoCovered = 0
}) => {
  const [selections, setSelections] = useState({});

  // Re-seeded whenever the phase's own commitment changes — including after a
  // confirm, which is what stops the badge from going stale.
  const ownKey = JSON.stringify(ownAlloc);
  React.useEffect(() => {
    const seeded = {};
    for (const [pid, m] of Object.entries(ownAlloc || {})) {
      if (Number(m) > 0) seeded[pid] = Number(m);
    }
    setSelections(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownKey]);

  const groups = useMemo(() => {
    const perRope = (ropeSpecs || []).filter(s => s && s.ropeIndex != null);
    if (!perRope.length) {
      return [
        {
          key: 'all',
          ropeIndex: null,
          label: null,
          // The specs know; `totalNeeded` is the fallback for a phase that has
          // no rope list at all.
          needed: Number(ropeSpecs?.[0]?.needed) || totalNeeded,
          reels: available
        }
      ];
    }
    return perRope.map(spec => ({
      key: `rope-${spec.ropeIndex}`,
      ropeIndex: spec.ropeIndex,
      label: spec.label,
      structure: spec.structure,
      needed: Number(spec.needed) || 0,
      reels: available.filter(p => p.ropeIndex === spec.ropeIndex)
    }));
  }, [ropeSpecs, available, totalNeeded]);

  /** Selected metres, counted only within one rope's reels. */
  const selectedFor = (group, sel = selections) =>
    group.reels.reduce((s, p) => s + (Number(sel[p.phantomId]) || 0), 0);

  const togglePhantom = (phantomId, maxLen) => {
    setSelections(prev => {
      if (prev[phantomId] !== undefined) {
        const next = { ...prev };
        delete next[phantomId];
        return next;
      }
      // Default to what THIS rope still needs. Counting the whole phase's
      // selection here would offer the earth nothing once the main rope was
      // covered.
      const group =
        groups.find(g => g.reels.some(p => p.phantomId === phantomId)) || groups[0];
      const stillNeeded = Math.max(0, (group?.needed || 0) - selectedFor(group, prev));
      return { ...prev, [phantomId]: Math.min(maxLen, stillNeeded) };
    });
  };

  const updateAmount = (phantomId, val) => {
    setSelections(prev => ({ ...prev, [phantomId]: Math.max(0, Number(val) || 0) }));
  };

  const totalSelected = (sel) =>
    Object.values(sel || selections).reduce((s, v) => s + (Number(v) || 0), 0);

  const selected = totalSelected();
  const canConfirm = selected > 0;

  const handleConfirm = () => {
    const result = Object.entries(selections)
      .filter(([, len]) => len > 0)
      .map(([phantomId, useLength]) => {
        const ph = available.find(p => p.phantomId === phantomId);
        // A reel another product spent while this picker was open is gone from
        // `available`, and `{ ...undefined }` used to produce an execution with
        // no phantomId at all — metres from nowhere.
        if (!ph) return null;
        // Never more than the reel still holds after everyone else's claims.
        // Two pickers can be open on two products at once, and both read the
        // full reel until one of them confirms.
        const cap = Number(ph.effectiveAvailable ?? ph.availableLength) || 0;
        return { ...ph, useLength: Math.min(Number(useLength) || 0, cap) };
      })
      .filter(r => r && r.useLength > 0);
    if (result.length > 0) onUsePhantoms(result);
  };

  const multiRope = groups.length > 1;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        {multiRope ? (
          <small className="text-700">
            {groups.length} ropes on this cable — each needs its own section
          </small>
        ) : (
          <small className="text-700">
            Need: <b>{fmt(totalNeeded)} m</b> | Selected:{' '}
            <b
              className={
                selected + alsoCovered >= totalNeeded ? 'text-success' : 'text-warning'
              }
            >
              {fmt(selected)} m
            </b>
            {/* A share of a drum being wound covers the need just as a reel
                does — it is simply not selected HERE. */}
            {alsoCovered > 0 && (
              <span className="text-warning ms-1">+ {fmt(alsoCovered)} m claimed</span>
            )}
            {selected + alsoCovered < totalNeeded && (
              <span className="text-muted ms-1">(rest will be produced)</span>
            )}
          </small>
        )}
        {canConfirm && (
          <Button size="sm" variant="info" onClick={handleConfirm}>
            Use selected stock ({Object.keys(selections).filter(k => selections[k] > 0).length} reels)
          </Button>
        )}
      </div>

      {/* Per rope, because the two ropes of a reduced-earth cable are two
          different conductors: a reel of 16 mm² cannot go on the 35 mm² rope,
          and "Need" is a different number for each. */}
      {groups.map(group => (
        <div key={group.key} className={multiRope ? 'mb-3' : ''}>
          {multiRope && (
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="fw-semibold">
                {group.label}
                {group.structure && <span className="text-muted ms-1">{group.structure}</span>}
              </small>
              <small className="text-700">
                Need: <b>{fmt(group.needed)} m</b> | Selected:{' '}
                <b className={selectedFor(group) >= group.needed ? 'text-success' : 'text-warning'}>
                  {fmt(selectedFor(group))} m
                </b>
              </small>
            </div>
          )}
          {multiRope && !group.reels.length && (
            <div className="text-muted fs-10 mb-1">
              No stock reel of this section — this rope will be produced.
            </div>
          )}
          <Row className="g-2">
        {group.reels.map(ph => {
          const isSelected = selections[ph.phantomId] !== undefined;
          const wType = String(ph.wireType || '').toUpperCase();
          const wDiam = Array.isArray(ph.wireDiameter) ? ph.wireDiameter.join(', ') : ph.wireDiameter || '';

          return (
            <Col xs={12} sm={6} md={4} key={ph.phantomId}>
              <div
                className={`p-2 rounded border ${isSelected ? 'border-success bg-success-subtle' : 'border-info bg-white'}`}
                style={{ fontSize: 12 }}
              >
                <div
                  className="d-flex justify-content-between align-items-center mb-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => togglePhantom(ph.phantomId, ph.effectiveAvailable ?? ph.availableLength)}
                >
                  <Badge bg={isSelected ? 'success' : 'info'} style={{ fontSize: 10 }}>
                    {isSelected ? 'SELECTED' : 'STOCK'}
                  </Badge>
                  <span className="fw-bold">{fmt(ph.effectiveAvailable ?? ph.availableLength)} m available</span>
                </div>
                <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                  {wType && <span className="me-1">{wType}</span>}
                  {wDiam && <span className="me-1">Ø{wDiam}</span>}
                  sect. {ph.section}
                </div>
                {/* Who is already on this reel. One physical bobbin can feed
                    several products in one launch, and without this the planner
                    could not tell a reel nobody had touched from one with 13 m
                    left — the card looked the same either way. */}
                {(takenBy[ph.phantomId] || []).length > 0 && (
                  <div className="mb-1" style={{ fontSize: 10 }}>
                    {(takenBy[ph.phantomId] || []).map((t, i) => (
                      <div key={i} className={t.own ? 'text-info' : 'text-warning'}>
                        {fmt(t.metres)} m → {t.own ? 'this phase' : t.label}
                      </div>
                    ))}
                    <div className="text-700">
                      {fmt(ph.effectiveAvailable ?? ph.availableLength)} m left of{' '}
                      {fmt(ph.availableLength)} m
                    </div>
                  </div>
                )}
                {ph.note && (
                  <div className="text-muted mb-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
                    {ph.note}
                  </div>
                )}
                {isSelected && (
                  <Form.Group className="mt-1">
                    <Form.Label className="mb-0" style={{ fontSize: 10 }}>Use (m):</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={ph.effectiveAvailable ?? ph.availableLength}
                      value={selections[ph.phantomId] || ''}
                      onChange={e => updateAmount(ph.phantomId, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: 12 }}
                    />
                  </Form.Group>
                )}
              </div>
            </Col>
          );
        })}
          </Row>
        </div>
      ))}
    </>
  );
};

// The in-flight block, with its own selection state.
//
// Separate from `MultiSelectPicker` on purpose: these two confirms write two
// DIFFERENT things — a finished reel becomes a phantom execution born
// COMPLETED, a drum still being wound becomes a CLAIM with no machine. Folding
// them into one picker is how one of the two ends up writing the other's shape,
// and claiming unmade metres as stock is the mistake that invents copper.
const IncomingRopePicker = ({ drums, ownClaims = {}, takenBy = {}, needed = 0, onUse }) => {
  const [selections, setSelections] = useState({});

  // Re-seeded from what this phase has already committed, so a second visit
  // shows the claims as selections rather than as nothing — confirming
  // replaces every claim execution on the phase at once.
  const ownKey = JSON.stringify(ownClaims);
  React.useEffect(() => {
    const seeded = {};
    for (const [tid, m] of Object.entries(ownClaims || {})) {
      if (Number(m) > 0) seeded[tid] = Number(m);
    }
    setSelections(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownKey]);

  const toggle = drum => {
    setSelections(prev => {
      if (prev[drum.tokenId] !== undefined) {
        const next = { ...prev };
        delete next[drum.tokenId];
        return next;
      }
      // WHAT IS STILL MISSING, not the whole drum.
      //
      // Defaulting to the drum would quietly take the donor's lot to zero: a
      // 30 000 m rope drum opened on a product that needs 17 500 more would
      // hand over every metre of somebody else's stock run. `needed` already
      // has the stock reels this phase committed subtracted from it, so this is
      // the rope the product genuinely still has to come from somewhere.
      const taken = Object.values(prev).reduce((s, v) => s + (Number(v) || 0), 0);
      const stillNeeded = Math.max(0, needed - taken);
      return {
        ...prev,
        [drum.tokenId]: stillNeeded > 0 ? Math.min(drum.freeLength, stillNeeded) : drum.freeLength
      };
    });
  };

  const setAmount = (drum, val) =>
    setSelections(prev => ({
      ...prev,
      [drum.tokenId]: Math.max(0, Number(val) || 0)
    }));

  const chosen = Object.entries(selections).filter(([, m]) => Number(m) > 0);
  const selectedTotal = chosen.reduce((s, [, m]) => s + Number(m), 0);

  const confirm = () => {
    const result = chosen
      .map(([tokenId, take]) => {
        const d = drums.find(x => x.tokenId === tokenId);
        if (!d) return null;
        // Never more than the drum is planned to hold after everyone else's
        // claims — the same cap the stock reels get, for the same reason.
        return { ...d, useLength: Math.min(Number(take) || 0, d.freeLength) };
      })
      .filter(r => r && r.useLength > 0);
    if (result.length) onUse(result);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
        <small className="fw-semibold">
          Rope being made — not stock yet
          <Badge bg="warning" className="ms-2">{drums.length}</Badge>
        </small>
        <small className="text-700">
          Planned: <b>{fmt(drums.reduce((s, d) => s + Number(d.freeLength || 0), 0))} m</b>
          {selectedTotal > 0 && (
            <>
              {' '}
              | Taking: <b className="text-success">{fmt(selectedTotal)} m</b>
            </>
          )}
        </small>
      </div>

      {/* What claiming MEANS, said once, above the cards. The difference from a
          stock reel is only WHEN: the metres are owed rather than wound. */}
      <div className="text-700 mb-2" style={{ fontSize: 10 }}>
        These drums are already launched and already queued, so claiming from one
        schedules nothing — no run is added for this product. The drum is wound
        exactly as it was planned, and its metres are handed over when it comes
        off.
      </div>

      <Row className="g-2">
        {drums.map(d => (
          <Col xs={12} sm={6} md={4} key={d.tokenId}>
            <IncomingRopeCard
              drum={d}
              selected={selections[d.tokenId] !== undefined}
              amount={selections[d.tokenId]}
              takenBy={takenBy[d.tokenId] || []}
              onToggle={toggle}
              onAmount={setAmount}
            />
          </Col>
        ))}
      </Row>

      {chosen.length > 0 && (
        <div className="mt-2 text-end">
          <Button size="sm" variant="warning" onClick={confirm}>
            Claim from {chosen.length} {chosen.length === 1 ? 'drum' : 'drums'}
          </Button>
        </div>
      )}
    </>
  );
};

const PhantomTokenPicker = ({
  phaseName,
  section,
  wireSpecs,
  phaseData,
  totalNeeded,
  // One spec per rope of a mixed pre-stranding — see helpers/phantomRopeSpecs.js.
  // A cable with a reduced earth is stranded twice, at two different sections,
  // and `/phantoms/compatible` matches pre-stranding stock BY SECTION. Asking
  // once with the main section is why the 16 mm² reels were never offered.
  ropeSpecs = null,
  onUsePhantom,
  onUsePhantoms,
  usedPhantomIds = [],
  // Metres claimed by OTHER products/phases in this wizard, per reel.
  phantomAllocMap = {},
  // Metres this very product+phase has committed, per reel.
  ownAllocMap = {},
  // { phantomId: [{ label, metres, own }] } — who is on the reel, for the card.
  phantomTakenBy = {},
  // Rope still being made: what this phase has already taken, per drum, and
  // where a confirmed selection goes. Absent = reported but not takeable.
  ownClaimMap = {},
  // …and what OTHER products in this launch have taken off those drums, plus
  // who they are. One bobbin can be offered to several products in one submit.
  incomingAllocMap = {},
  incomingTakenBy = {},
  onUseIncomingRopes = null
}) => {
  const params = useMemo(() => {
    const p = { phase: phaseName };
    if (section) p.section = section;

    // for pre_stranding, pass wire specs from phase data for precise matching
    if (phaseName === 'pre_stranding' && phaseData) {
      if (phaseData.type) p.wireType = phaseData.type;
      if (phaseData.wireDiameter) p.wireDiameter = JSON.stringify(phaseData.wireDiameter);
      if (phaseData.numberOfWires) p.numberOfWires = JSON.stringify(phaseData.numberOfWires);
    }

    return p;
  }, [phaseName, section, phaseData]);

  // One query per rope, so each rope asks for its OWN section. The spec that
  // asked is remembered on every reel it brought back: that is how a selection
  // knows which rope's execution it becomes, and it is more reliable than
  // matching the reel back to a rope by wire diameter afterwards — the endpoint
  // ignores diameters on purpose.
  const perRope = useMemo(() => {
    if (phaseName !== 'pre_stranding') return [];
    return (ropeSpecs || []).filter(s => s && s.ropeIndex != null);
  }, [phaseName, ropeSpecs]);

  const ropeQueries = useQueries({
    queries: perRope.map(spec => {
      const p = { phase: 'pre_stranding' };
      if (spec.section) p.section = spec.section;
      if (spec.wireType) p.wireType = spec.wireType;
      return {
        queryKey: ['phantoms', 'compatible', p],
        queryFn: () => fetchCompatible(p),
        refetchInterval: 15000,
        staleTime: 10000
      };
    })
  });

  const singleQuery = useQuery({
    queryKey: ['phantoms', 'compatible', params],
    queryFn: () => fetchCompatible(params),
    enabled: !!phaseName && perRope.length === 0,
    refetchInterval: 15000,
    staleTime: 10000
  });

  // ── The same question, asked of rope that is still being made ───────────
  //
  // One query per rope for exactly the reason the stock ones are: each rope has
  // its OWN section, and the match is on section. The flat case asks once with
  // the phase's own section, which is what `params` already carries.
  //
  // pre_stranding only — copper rope is the only semifabricate held this way.
  const ropeStockParams = useMemo(() => {
    if (phaseName !== 'pre_stranding') return [];
    if (perRope.length) {
      return perRope.map(spec => ({
        ropeIndex: spec.ropeIndex,
        ropeLabel: spec.label,
        query: {
          ...(spec.section ? { section: spec.section } : {}),
          ...(spec.wireType ? { wireType: spec.wireType } : {})
        }
      }));
    }
    return [
      {
        ropeIndex: null,
        ropeLabel: null,
        query: {
          ...(params.section ? { section: params.section } : {}),
          ...(params.wireType ? { wireType: params.wireType } : {})
        }
      }
    ];
  }, [phaseName, perRope, params]);

  const incomingQueries = useQueries({
    queries: ropeStockParams.map(p => ({
      queryKey: ['phantoms', 'incoming-ropes', p.query],
      queryFn: () => fetchIncomingRopes(p.query),
      refetchInterval: 15000,
      staleTime: 10000
    }))
  });

  const incoming = useMemo(() => {
    // One drum answers one rope, like the reels above: a drum offered under two
    // ropes would read as twice the rope that is coming.
    const seen = new Set();
    const out = [];
    ropeStockParams.forEach((p, i) => {
      for (const d of incomingQueries[i]?.data || []) {
        if (seen.has(d.tokenId)) continue;
        seen.add(d.tokenId);

        // WHAT IS LEFT OF IT, after everyone else in this launch.
        //
        // `freeLength` from the server is the drum's own answer — its plan less
        // what is reserved and drawn off. It knows nothing about a product
        // being planned in the wizard beside this one, so the 3x6 was offered
        // all 30 000 m of a drum the 5x6 had taken 17 500 of, and the two
        // shares together would have re-planned a 30 000 m bobbin to 47 500.
        //
        // This phase's OWN take is deliberately not subtracted: a confirm
        // replaces every share on the phase, so those metres are still its to
        // spend — the same rule the stock reels follow.
        const takenByOthers = Number(incomingAllocMap[d.tokenId] || 0);
        const freeLength = Math.max(0, Number(d.freeLength || 0) - takenByOthers);

        // A drum with nothing left is dropped, unless this phase is the one
        // holding it — then it has to stay on screen to be adjusted.
        if (freeLength <= 0 && !(Number(ownClaimMap[d.tokenId]) > 0)) continue;

        out.push({
          ...d,
          freeLength,
          takenByOthers,
          ropeIndex: p.ropeIndex,
          ropeLabel: p.ropeLabel
        });
      }
    });
    return out;
  }, [ropeStockParams, incomingQueries, incomingAllocMap, ownClaimMap]);

  const incomingFree = incoming.reduce((s, d) => s + Number(d.freeLength || 0), 0);

  const { phantoms, isLoading } = useMemo(() => {
    if (!perRope.length) {
      return { phantoms: singleQuery.data || [], isLoading: singleQuery.isLoading };
    }

    // A reel that fits two ropes is shown under the first that asked for it:
    // one physical reel cannot feed both, and offering it twice would let the
    // same metres be spent twice over.
    const seen = new Set();
    const out = [];
    perRope.forEach((spec, i) => {
      for (const ph of ropeQueries[i]?.data || []) {
        if (seen.has(ph.phantomId)) continue;
        seen.add(ph.phantomId);
        out.push({ ...ph, ropeIndex: spec.ropeIndex, ropeLabel: spec.label, ropeStructure: spec.structure });
      }
    });

    return { phantoms: out, isLoading: ropeQueries.some(q => q.isLoading) };
  }, [perRope, ropeQueries, singleQuery.data, singleQuery.isLoading]);

  const available = useMemo(() => {
    return phantoms
      .map(p => {
        // What OTHER products and phases in this wizard have already claimed.
        // This phase's own claim is deliberately NOT subtracted: confirming
        // replaces every phantom execution on the phase, so its own metres are
        // still its to spend. Subtracting them is what made a reel the planner
        // had just used read "13.5 m available" with no way back.
        const allocated = phantomAllocMap[p.phantomId] || 0;
        const effectiveAvailable = Math.max(0, p.availableLength - allocated);
        return { ...p, effectiveAvailable };
      })
      .filter(p => {
        // A reel with nothing left is dropped — unless this phase is the one
        // holding it, in which case it has to stay on screen to be adjusted.
        if (p.effectiveAvailable <= 0 && !(ownAllocMap[p.phantomId] > 0)) return false;
        if (phaseName === 'conductor_isolation' && wireSpecs?.length) {
          return isWireCompatible(p, wireSpecs);
        }
        return true;
      });
  }, [phantoms, phaseName, wireSpecs, phantomAllocMap, ownAllocMap]);

  if (isLoading) {
    return (
      <div className="text-muted fs-10 py-1">
        <Spinner animation="border" size="sm" className="me-1" />
        Checking stock...
      </div>
    );
  }

  // Rope on its way is a reason to open this card on its own: with no stock at
  // all, knowing 53 km is coming is the whole point.
  if (!available.length && !incoming.length) return null;

  const isMultiSelect = phaseName === 'pre_stranding';

  return (
    <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: '5px solid #0dcaf0' }}>
      <Card.Header className="bg-info-subtle fw-semibold py-2 d-flex justify-content-between align-items-center">
        <span>
          {isMultiSelect ? 'Available Stock Reels' : 'Available Phantom Tokens'}
          <Badge bg="info" className="ms-2">{available.length}</Badge>
          {/* Two counts, because they are two different facts: metres that
              exist, and metres that are being made. One badge reading "6"
              would be neither. */}
          {incoming.length > 0 && (
            <Badge bg="warning" className="ms-1">
              +{incoming.length} being made
            </Badge>
          )}
        </span>
        <span className="text-muted fs-10">
          {isMultiSelect ? 'Select reels and quantities to use from stock' : 'Click to use as input'}
        </span>
      </Card.Header>
      <Card.Body className="pt-2 pb-2">
        {isMultiSelect ? (
          <MultiSelectPicker
            available={available}
            totalNeeded={totalNeeded || 0}
            onUsePhantoms={onUsePhantoms || (() => {})}
            usedPhantomIds={usedPhantomIds}
            ropeSpecs={perRope.length ? perRope : ropeSpecs}
            ownAlloc={ownAllocMap}
            takenBy={phantomTakenBy}
            alsoCovered={Object.values(ownClaimMap || {}).reduce(
              (s, v) => s + (Number(v) || 0),
              0
            )}
          />
        ) : (
          <Row className="g-2">
            {available.map(ph => (
              <Col xs={12} sm={6} md={4} key={ph.phantomId}>
                <SingleSelectCard phantom={ph} onUsePhantom={onUsePhantom} />
              </Col>
            ))}
          </Row>
        )}

        {/* ── ROPE BEING MADE ─────────────────────────────────────────────
            Below the reels and separated from them, because what it is good
            for is a DECISION, not a selection: a fourth rope run is the
            wrong move when 53 km is already on the line and in the queue.
            Reported for pre-stranding only — rope is the only semifabricate
            held as stock. */}
        {incoming.length > 0 && (
          <div className="mt-3 pt-2 border-top">
            {onUseIncomingRopes ? (
              <IncomingRopePicker
                drums={incoming}
                ownClaims={ownClaimMap}
                takenBy={incomingTakenBy}
                // What is still missing after the stock reels this phase has
                // already committed — so opening a drum defaults to the rope
                // that genuinely has to come from somewhere, not to the
                // donor's whole run.
                needed={Math.max(
                  0,
                  Number(totalNeeded || 0) -
                    Object.values(ownAllocMap || {}).reduce(
                      (s, v) => s + (Number(v) || 0),
                      0
                    )
                )}
                onUse={onUseIncomingRopes}
              />
            ) : (
              // No handler = reported only. Every phase but pre-stranding, and
              // any caller that has not wired the share up: better a card that
              // cannot be clicked than one that silently does nothing.
              <>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                  <small className="fw-semibold">
                    Rope being made — not stock yet
                    <Badge bg="warning" className="ms-2">{incoming.length}</Badge>
                  </small>
                  <small className="text-700">
                    Coming: <b>{fmt(incomingFree)} m</b>
                    <span className="text-muted ms-1">
                      — each becomes a stock reel when its drum is finished
                    </span>
                  </small>
                </div>
                <Row className="g-2">
                  {incoming.map(d => (
                    <Col xs={12} sm={6} md={4} key={d.tokenId}>
                      <IncomingRopeCard drum={d} selected={false} onToggle={null} />
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PhantomTokenPicker;
